import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Copy, Check, Tv2, ArrowLeft } from 'lucide-react';
import { playerScreenUrl } from '@/shared/lib/playerUrl';
import { useTenantHomePath } from '@/shared/hooks/useTenantHomePath';

interface PlayerMobileGateProps {
  telaId: string;
}

/**
 * Diagnóstico dos sinais usados por useIsMobileViewport — visível direto
 * nesta tela pra facilitar identificar por que um dispositivo caiu aqui
 * (ex: TV Box com DPI alto sendo confundida com celular).
 */
function diagnostico() {
  return {
    innerWidth: window.innerWidth,
    matchMediaLg1023: window.matchMedia('(max-width: 1023px)').matches,
    devicePixelRatio: window.devicePixelRatio,
    ontouchstart: 'ontouchstart' in window,
    maxTouchPoints: navigator.maxTouchPoints,
    userAgent: navigator.userAgent,
  };
}

/** Bloqueia o player em viewport mobile — exibe link para a TV Box. */
export default function PlayerMobileGate({ telaId }: PlayerMobileGateProps) {
  const navigate = useNavigate();
  // Sem dados da tela aqui de propósito: o gate não monta nada do Player.
  const { path: homePath } = useTenantHomePath();
  const url = playerScreenUrl(telaId);
  const [copiado, setCopiado] = useState(false);
  const [diag]  = useState(diagnostico);

  const copiar = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      /* ignore */
    }
  }, [url]);

  return (
    <div className="fixed inset-0 flex flex-col bg-gray-950 px-4 py-8 text-white">
      <button
        type="button"
        onClick={() => navigate(homePath)}
        className="mb-6 inline-flex items-center gap-2 self-start rounded-xl bg-white/[0.06] px-3 py-2 text-xs font-semibold text-slate-400"
      >
        <ArrowLeft className="h-4 w-4" />
        Voltar ao painel
      </button>

      <div className="mx-auto flex max-w-md flex-1 flex-col justify-center text-center">
        <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-orange-500/15 text-orange-400">
          <Tv2 className="h-8 w-8" aria-hidden="true" />
        </div>
        <h1 className="text-xl font-bold">Use na TV Box</h1>
        <p className="mt-2 text-sm leading-relaxed text-slate-400">
          Esta tela só funciona no navegador da TV do salão. Copie o endereço abaixo e cole no
          Chrome da TV.
        </p>
        <p className="mt-6 break-all rounded-xl border border-white/10 bg-white/[0.04] px-3 py-3 font-mono text-xs text-slate-300">
          {url}
        </p>
        <button
          type="button"
          onClick={copiar}
          className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-orange-500 py-3.5 text-sm font-semibold text-white active:scale-[0.98]"
        >
          {copiado ? (
            <>
              <Check className="h-4 w-4" />
              Copiado
            </>
          ) : (
            <>
              <Copy className="h-4 w-4" />
              Copiar link
            </>
          )}
        </button>

        <div className="mt-8 rounded-xl border border-white/[0.06] bg-white/[0.02] px-3 py-2.5 text-left font-mono text-[10px] leading-relaxed text-slate-600">
          <p>innerWidth: {diag.innerWidth} · DPR: {diag.devicePixelRatio}</p>
          <p>viewport &lt; 1024px: {String(diag.matchMediaLg1023)}</p>
          <p>ontouchstart: {String(diag.ontouchstart)} · maxTouchPoints: {diag.maxTouchPoints}</p>
          <p className="break-all">UA: {diag.userAgent}</p>
        </div>
      </div>
    </div>
  );
}
